<template>
    <div class="add-employee">
        <div class="card mb-4">
            <div class="card-header">
                <h3>Add Employee</h3>
            </div>
            <div class="card-body">
                <form @submit.prevent="handleSubmit">
                    <div class="mb-3">
                        <label class="form-label">Designation</label>
                        <select 
                            class="form-select" 
                            v-model="selectedDesignation"
                            @change="handleDesignationChange"
                            required
                        >
                            <option value="">Select Designation</option>
                            <option 
                                v-for="designation in designations" 
                                :key="designation._id"
                                :value="designation"
                            >
                                {{ designation.name }}
                            </option>
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Employees</label>
                        <select 
                            class="form-select" 
                            v-model="selectedEmployees"
                            multiple
                            size="5"
                            :disabled="!selectedDesignation"
                            required
                        >
                            <option 
                                v-for="employee in availableEmployees" 
                                :key="employee._id"
                                :value="employee"
                            >
                                {{ employee.employeeName }} ({{ employee.employeeCode }})
                            </option>
                        </select>
                        <small class="text-muted d-block mt-1">Hold Ctrl/Cmd to select multiple employees</small>
                        <small class="text-muted" v-if="!selectedDesignation">Please select a designation first</small>
                        <div v-if="selectedEmployees.length > 0" class="mt-2">
                            <p class="mb-2">Selected Employees ({{ selectedEmployees.length }}):</p>
                            <div class="selected-employees">
                                <div 
                                    v-for="emp in selectedEmployees" 
                                    :key="emp._id"
                                    class="selected-employee"
                                >
                                    {{ emp.employeeName }}
                                    <button 
                                        type="button" 
                                        class="btn-close ms-2"
                                        @click="removeFromSelection(emp)"
                                        aria-label="Remove"
                                    ></button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="d-flex justify-content-end gap-2">
                        <button 
                            type="button" 
                            class="btn btn-secondary"
                            @click="router.back()"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            class="btn btn-primary"
                            :disabled="selectedEmployees.length === 0 || isAddingEmployees"
                        >
                            <span v-if="isAddingEmployees" class="spinner-border spinner-border-sm me-1" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </span>
                            Add Employees ({{ selectedEmployees.length }})
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h3>Existing Employees</h3>
                <div class="input-group w-auto">
                    <input 
                        type="text" 
                        class="form-control" 
                        v-model="searchQuery"
                        placeholder="Search employees..."
                    >
                </div>
            </div>
            <div class="card-body">
                <div class="table-responsive">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Employee Code</th>
                                <th>Name</th>
                                <th>Designation</th>
                                <th>Phone Number</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="employee in filteredEmployees" :key="employee._id">
                                <td>{{ employee.employeeCode }}</td>
                                <td>{{ employee.employeeName }}</td>
                                <td>{{ employee.designation?.name || 'N/A' }}</td>
                                <td>{{ employee.permanentAddress?.phoneNumber || 'N/A' }}</td>
                                <td>
                                    <button 
                                        class="btn btn-sm btn-danger d-flex align-items-center gap-1"
                                        @click="removeEmployeeWithLoading(employee._id)"
                                        :disabled="removingEmployeeId === employee._id"
                                    >
                                        <span v-if="removingEmployeeId === employee._id" class="spinner-border spinner-border-sm" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </span>
                                        <i v-else class="bi bi-trash"></i>
                                        Remove
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="isLoading" class="text-center my-4">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>

                <div v-if="!isLoading && employees.length === 0" class="text-center my-4">
                    <p class="text-muted">No employees found</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Swal from 'sweetalert2';

const router = useRouter();
const route = useRoute();
const siteId = route.params.siteId as string;
const baseUrl = process.env.VUE_APP_BASE_URL;

const formData = ref({
    name: '',
});

interface Designation {
    _id: string;
    name: string;
}

interface Employee {
    _id: string;
    employeeName: string;
    employeeCode: string;
    employeeType: string;
    site: any | null;
    designation?: {
        _id: string;
        name: string;
    };
    permanentAddress?: {
        village?: string;
        postOffice?: string;
        policeStation?: string;
        district?: string;
        state?: string;
        pin?: string;
        phoneNumber?: string;
        alternatePhoneNumber?: string;
        alternatePhoneRelation?: string;
        _id?: string;
    };
}

const employees = ref<Employee[]>([]);
const isLoading = ref(false);
const searchQuery = ref('');
const searchResults = ref<Employee[]>([]);
const selectedEmployees = ref<Employee[]>([]);
const removingEmployeeId = ref<string | null>(null);
const designations = ref<Designation[]>([]);
const selectedDesignation = ref<Designation | null>(null);
const allEmployees = ref<Employee[]>([]);
const isAddingEmployees = ref(false);

// Get unique designations from employees
const fetchEmployeesAndDesignations = async () => {
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}api/joining/employees`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        const result = await response.json();
        if (result.success) {
            allEmployees.value = result.data;
            // Extract unique designations from employees
            const uniqueDesignations = new Map();
            result.data.forEach((emp: Employee) => {
                if (emp.designation) {
                    uniqueDesignations.set(emp.designation._id, emp.designation);
                }
            });
            designations.value = Array.from(uniqueDesignations.values());
        }
    } catch (error) {
        console.error('Error fetching employees:', error);
    }
};

// Computed property for available employees based on designation
const availableEmployees = computed(() => {
    if (!selectedDesignation.value) return [];
    return allEmployees.value.filter(emp => 
        emp.designation?._id === selectedDesignation.value?._id && !emp.site
    );
});

// Handle designation change
const handleDesignationChange = () => {
    selectedEmployees.value = [];
};

// Remove employee from selection
const removeFromSelection = (employee: Employee) => {
    selectedEmployees.value = selectedEmployees.value.filter(emp => emp._id !== employee._id);
};

// Update handleSubmit to handle multiple employees
const handleSubmit = async () => {
    if (selectedEmployees.value.length === 0) return;
    
    isAddingEmployees.value = true;
    const errors: string[] = [];
    
    try {
        // Add employees one by one
        for (const employee of selectedEmployees.value) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${baseUrl}api/sites/assign/employee/${siteId}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        employeeIds: employee._id
                    })
                });
                const result = await response.json();
                
                if (!result.success) {
                    errors.push(`Failed to add ${employee.employeeName}: ${result.message}`);
                }
            } catch (error) {
                errors.push(`Failed to add ${employee.employeeName}: Network error`);
            }
        }
        
        await fetchEmployees();
        await fetchEmployeesAndDesignations();
        
        // Reset form
        selectedEmployees.value = [];
        selectedDesignation.value = null;
        
        if (errors.length > 0) {
            Swal.fire({
                title: 'Partial Success',
                html: `Some employees could not be added:<br>${errors.join('<br>')}`,
                icon: 'warning'
            });
        } else {
            Swal.fire({
                title: 'Success!',
                text: 'All employees added successfully',
                icon: 'success'
            });
        }
    } catch (error) {
        console.error('Error adding employees:', error);
        Swal.fire({
            title: 'Error!',
            text: 'Failed to add employees',
            icon: 'error'
        });
    } finally {
        isAddingEmployees.value = false;
    }
};

// Filter employees based on search
const filteredEmployees = computed(() => {
    return employees.value.filter(employee => 
        employee.employeeName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        employee.employeeCode.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        employee.designation?.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        employee.permanentAddress?.phoneNumber?.includes(searchQuery.value) || false
    );
});

// Fetch employees list
const fetchEmployees = async () => {
    isLoading.value = true;
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}api/sites/get/employee/${siteId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        
        const result = await response.json();
        
        if (result.success && Array.isArray(result.data)) {
            employees.value = result.data;
        } else {
            console.error('Invalid API response format:', result);
            employees.value = [];
        }
    } catch (error) {
        console.error('Error fetching employees:', error);
        employees.value = [];
    } finally {
        isLoading.value = false;
    }
};

// Remove employee with loading state
const removeEmployeeWithLoading = async (employeeId: string) => {
    const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You want to remove this employee from the site?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, remove!',
        cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;
    
    removingEmployeeId.value = employeeId;
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}api/sites/remove/employee/${siteId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                employeeIds: employeeId
            })
        });
        
        const apiResult = await response.json();
        
        if (apiResult.success) {
            await fetchEmployees();
            Swal.fire(
                'Removed!',
                'Employee has been removed from the site.',
                'success'
            );
        } else {
            Swal.fire(
                'Error!',
                apiResult.message || 'Failed to remove employee',
                'error'
            );
        }
    } catch (error) {
        console.error('Error removing employee:', error);
        Swal.fire(
            'Error!',
            'Failed to remove employee from site',
            'error'
        );
    } finally {
        removingEmployeeId.value = null;
    }
};

// Initialize component
onMounted(() => {
    fetchEmployees();
    fetchEmployeesAndDesignations();
});
</script>

<style scoped>
.table th {
    background-color: #f8f9fa;
}

.btn-danger {
    padding: 0.25rem 0.5rem;
    white-space: nowrap;
}

.gap-1 {
    gap: 0.25rem;
}

.search-results {
    position: absolute;
    background: white;
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.cursor-pointer {
    cursor: pointer;
}

.hover\:bg-gray-100:hover {
    background-color: #f3f4f6;
}

@media (max-width: 768px) {
    .card-header {
        flex-direction: column;
        gap: 1rem;
    }
    
    .input-group {
        width: 100% !important;
    }
    
    .table-responsive {
        margin: 0 -1rem;
    }
}

.selected-employees {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.selected-employee {
    display: inline-flex;
    align-items: center;
    background-color: #e9ecef;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
}

.selected-employee .btn-close {
    font-size: 0.75rem;
    padding: 0.25rem;
}

select[multiple] {
    height: auto;
    min-height: 200px;
}

@media (max-width: 768px) {
    select[multiple] {
        min-height: 150px;
    }
    
    .selected-employee {
        width: 100%;
        justify-content: space-between;
    }
}
</style>